const getPipelinesUrls = require('../common/getPipelinesUrls');
const { urlWithSearchParams } = require('./urlUtilities');

const pipelinesUrls = getPipelinesUrls();

/**
 * Fetch the contact key associated to an email address
 * @param {string} emailAddress The email of the guest user
 * @returns {string|null} Contact key
 */
async function getContactKeyForEmailAddress(emailAddress) {
    var response = await fetch(
        urlWithSearchParams(pipelinesUrls.sfmcGetContactKey, {
            email: emailAddress
        })
    );
    if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
    }
    var responseData = await response.json();
    if (responseData.ok) {
        return responseData.contactKey;
    }
    return null;
}

/**
 * Creates a temporary contact in Salesforce Marketing Cloud (SFMC)
 *
 * This function creates temporary contacts in SFMC that are
 * automatically deleted after a short period. Originally designed for
 * abandoned cart emails to guest users, this mechanism is also used as
 * a workaround to send transactional emails (like cart management) to
 * registered users who have not opted in to marketing communications.
 *
 * **Use cases:**
 * - Guest users: Creates temporary contact for abandoned cart recovery
 * - Registered users without consent: Bypasses the main contact's
 *   subscription status to enable abandoned cart recovery communications
 *
 * **Important notes:**
 * - Guest contacts are automatically purged after a defined retention
 *   period
 * - For registered users, this creates a separate temporary contact
 *   independent of their main SFMC record
 * - This approach allows sending cart-related emails even when the
 *   user's primary contact is unsubscribed
 *
 * @param {string} email - The email address for the temporary contact
 * @param {boolean} isRegisteredUser - Indicates if this is for a
 *   registered user (true) or guest (false)
 * @returns {Promise<Object>} Server response containing the contact
 *   creation result
 *
 * @example
 * // Create guest contact for abandoned cart
 * await createTemporaryContact('guest@example.com', false);
 *
 * @example
 * // Create temporary contact for registered user without consent
 * await createTemporaryContact('user@example.com', true);
 */
async function createTemporaryContact(email, isRegisteredUser) {
    const body = new URLSearchParams({
        abandonedCartEmail: email,
        isRegisteredUser: isRegisteredUser
    });

    const response = await fetch(pipelinesUrls.sfmcCreateTemporaryContact, {
        method: 'POST',
        body
    });

    if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
    }

    return response.json();
}

/**
 * Updates the `AbandonedCart_GuestData` DE row for an existing temporary
 * contact. Used when a registered user logs in and already has an `abcart_`
 * contact — only the DE data needs updating, not the SFMC contact itself.
 *
 * @param {string} email - Email address identifying the DE row.
 * @param {boolean} isRegisteredUser - Should be `true` when called on login.
 * @returns {Promise<Object>} Server response with `ok` and `hasErrors`.
 */
async function updateTemporaryContact(email, isRegisteredUser) {
    const body = new URLSearchParams({
        abandonedCartEmail: email,
        isRegisteredUser: isRegisteredUser
    });

    const response = await fetch(pipelinesUrls.sfmcUpdateTemporaryContact, {
        method: 'POST',
        body
    });

    if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
    }

    return response.json();
}

module.exports = {
    createTemporaryContact: createTemporaryContact,
    getContactKeyForEmailAddress: getContactKeyForEmailAddress,
    updateTemporaryContact: updateTemporaryContact
};
